// Pièces d'un dossier : pour chaque pièce attendue (selon les réponses du formulaire), ce qui a
// été fourni et son verdict, et deux façons d'ajouter : depuis le coffre ou depuis l'appareil
// (photo ou fichier, PDF, JPEG ou PNG). Utilisé au dépôt et pour un complément.
import { Bandeau, Bouton, Selection, tailleLisible } from '@cc/ui';
import { Camera, Paperclip } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { CaptureDocument } from '../../../capture/CaptureDocument';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import { EtatRequete } from '../../../commun/EtatRequete';
import { useCoffre } from '../api';
import { TexteStatut } from '../commun';
import { FORMATS_PIECE, LIBELLES_VERDICT, TAILLE_MAX_PIECE, libelleTypePiece } from '../pieces';
import type { FicheDossierUsager, PieceCoffre } from '../types';
import type { PieceCoffreChoisie } from './useDepot';
import styles from './depot.module.css';
import commun from '../citoyen.module.css';

export interface ProprietesSelecteurPieces {
  dossier: Pick<FicheDossierUsager, 'id' | 'collectivite' | 'pieces' | 'pieces_attendues'>;
  /** Pièces du coffre choisies mais pas encore transmises (complément). */
  enAttente?: PieceCoffreChoisie[];
  onCoffre: (piece: PieceCoffreChoisie, titre: string) => Promise<unknown> | void;
  onFichier: (fichier: File, type: string) => Promise<unknown>;
  desactive?: boolean;
}

function CartePiece({
  attendue,
  dossier,
  coffre,
  enAttente,
  onCoffre,
  onFichier,
  desactive,
}: ProprietesSelecteurPieces & {
  attendue: FicheDossierUsager['pieces_attendues'][number];
  coffre: PieceCoffre[];
}) {
  const idFichier = useId();
  const { profil } = useSessionUsager();
  const [choixCoffre, setChoixCoffre] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const fournies = dossier.pieces.filter((p) => p.type === attendue.type);
  const attente = (enAttente ?? []).filter((p) => p.type === attendue.type);
  const memeType = coffre.filter((p) => p.type_piece === attendue.type && !p.expiree);
  const autres = coffre.filter((p) => p.type_piece !== attendue.type && !p.expiree);
  const options = [
    ...memeType.map((p) => ({
      valeur: p.id,
      libelle: `${p.titre}${p.proposee_au_depot ? ' (jugée conforme)' : ''}`,
    })),
    ...autres.map((p) => ({
      valeur: p.id,
      libelle: `${p.titre} (${libelleTypePiece(p.type_piece)})`,
    })),
  ];

  const envoyer = async (fichier: File) => {
    setErreur(null);
    if (fichier.size > TAILLE_MAX_PIECE) {
      setErreur(`Fichier trop volumineux (${tailleLisible(fichier.size)}) : 10 Mo au plus.`);
      return;
    }
    setEnvoi(true);
    try {
      await onFichier(fichier, attendue.type);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Envoi impossible');
    } finally {
      setEnvoi(false);
    }
  };

  const ajouterDuCoffre = async () => {
    const p = coffre.find((x) => x.id === choixCoffre);
    if (!p) return;
    setErreur(null);
    setEnvoi(true);
    try {
      await onCoffre({ piece_id: p.id, type: attendue.type }, p.titre);
      setChoixCoffre('');
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Ajout impossible');
    } finally {
      setEnvoi(false);
    }
  };

  const etat =
    attendue.fournie || attente.length > 0
      ? {
          libelle: attente.length > 0 && !attendue.fournie ? 'À transmettre' : 'Fournie',
          ton: 'ok' as const,
        }
      : attendue.obligatoire
        ? { libelle: 'Manquante', ton: 'attention' as const }
        : { libelle: 'Facultative', ton: 'neutre' as const };
  return (
    <li className={styles.piece}>
      <div className={styles.pieceEntete}>
        <span className={styles.pieceTitre}>{attendue.libelle}</span>
        <TexteStatut libelle={etat.libelle} ton={etat.ton} />
      </div>
      {fournies.map((p) => (
        <p key={p.id} className={styles.aide}>
          {p.titre} :{' '}
          <TexteStatut
            libelle={LIBELLES_VERDICT[p.verdict]?.libelle ?? p.verdict}
            ton={LIBELLES_VERDICT[p.verdict]?.ton ?? 'neutre'}
          />
          {p.motif ? ` (${p.motif})` : ''}
        </p>
      ))}
      {attente.map((p) => (
        <p key={p.piece_id} className={styles.aide}>
          Pièce du coffre choisie, transmise avec le complément.
        </p>
      ))}
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      <div className={styles.pieceActions}>
        <input
          id={idFichier}
          type="file"
          className={styles.fichierCache}
          accept={FORMATS_PIECE}
          aria-label={`Fichier pour ${attendue.libelle}`}
          disabled={desactive || envoi}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void envoyer(f);
          }}
        />
        <Bouton
          variante={attendue.fournie ? 'secondaire' : 'principal'}
          petit
          icone={Camera}
          disabled={desactive || envoi}
          chargement={envoi}
          libelleChargement="Envoi en cours"
          onClick={() => document.getElementById(idFichier)?.click()}
        >
          {attendue.fournie ? 'Ajouter une autre pièce' : "Photo ou fichier de l'appareil"}
        </Bouton>
        {profil ? (
          <CaptureDocument
            compteId={profil.id}
            espaceId={'citoyen:' + dossier.collectivite.code}
            usage={'dossier:' + dossier.id + ':' + attendue.type}
            disabled={desactive || envoi}
            onFichier={(f) => onFichier(f, attendue.type)}
          />
        ) : null}
        {options.length > 0 ? (
          <>
            <Selection
              libelle={`Depuis mon coffre, pour ${attendue.libelle}`}
              optionVide="Choisir une pièce du coffre"
              options={options}
              value={choixCoffre}
              onChange={(e) => setChoixCoffre(e.target.value)}
              disabled={desactive || envoi}
              classeGroupe={commun.pile}
            />
            <Bouton
              variante="secondaire"
              petit
              icone={Paperclip}
              disabled={!choixCoffre || desactive || envoi}
              onClick={() => void ajouterDuCoffre()}
            >
              Utiliser cette pièce
            </Bouton>
          </>
        ) : null}
      </div>
    </li>
  );
}

export function SelecteurPieces(p: ProprietesSelecteurPieces) {
  const coffre = useCoffre();
  if (p.dossier.pieces_attendues.length === 0)
    return <p className={commun.aide}>Aucune pièce n&apos;est attendue pour cette demande.</p>;
  return (
    <EtatRequete
      chargement={coffre.isPending}
      erreur={coffre.error}
      onReessayer={() => void coffre.refetch()}
      fonction="Le coffre de pièces"
      lignes={3}
    >
      <ul className={styles.lignes} aria-label="Pièces attendues">
        {p.dossier.pieces_attendues.map((a) => (
          <CartePiece key={a.type} attendue={a} coffre={coffre.data ?? []} {...p} />
        ))}
      </ul>
      <p className={commun.aide}>
        Les pièces ajoutées depuis l&apos;appareil restent dans ce dossier. Pour les réutiliser dans
        d&apos;autres démarches, ajoutez-les à <Link to="/citoyen/compte/pieces">votre coffre</Link>
        .
      </p>
    </EtatRequete>
  );
}
